/**
 * LineDrawer (S4.3) — the 訂單明細 row drill-down.
 *
 * Read-only by contract: the drawer renders the derived FulfillmentLine view
 * plus the `LinkedDocumentRail` (SO line ⇄ PO line ⇄ 裝櫃批次 ⇄ 收貨 ⇄ 發票,
 * navigable both ends) and the quantity-bucket event timeline. A1/A2/A3
 * checkpoint stamps ALWAYS render as the sanctioned bilingual labels from
 * `ofcVocabulary.ts` (ux-lens finding 7) — write-back happens in the owning
 * modules the rail links to, never here.
 */

import { useMemo } from 'react';
import { Alert, Drawer, Tag, Timeline, Typography, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import LinkedDocumentRail from '@/components/common/LinkedDocumentRail';
import type { LinkedDocumentRailItem } from '@/components/common/LinkedDocumentRail';
import QuantityBucketBar from '@/components/common/QuantityBucketBar';
import { useOccDocIndex, useOccNodeConfigs } from '@/hooks/useOcc';
import { usePoStore } from '@/stores/poStore';
import { useCheckpointLabel } from '@/components/procurement/PurchaseOrderDetailPage/checkpoints';
import type { OccLensLine } from '@/lib/occ/selectors';
import { LensFooter } from './lensShared';

const { Text, Title } = Typography;

export interface LineDrawerProps {
  open: boolean;
  line: OccLensLine | null;
  syncedAt?: string;
  onClose: () => void;
  /** Open the 裝櫃批次 drawer for a linked batch (rail stays in-workbench). */
  onOpenBatch: (batchId: string) => void;
}

interface TimelineEvent {
  key: string;
  label: string;
  qty: number;
  detail?: string;
  at?: string | null;
}

const LineDrawer = ({ open, line, syncedAt, onClose, onOpenBatch }: LineDrawerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const { nodeLabel } = useOccNodeConfigs();
  const docIndex = useOccDocIndex();
  const cpLabel = useCheckpointLabel();
  const checkpointRecords = usePoStore((state) => state.checkpointRecords);

  const poRefs = useMemo(() => {
    if (!line) return [];
    const refs = line.poLineIds
      .map((poLineId) => docIndex.poForPoLine(poLineId))
      .filter((po): po is { poId: string; poNumber: string } => Boolean(po));
    return [...new Map(refs.map((po) => [po.poId, po])).values()];
    // docIndex is rebuilt per render from cached reads; poLineIds is the driver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  // The record stays mounted while the closing animation runs (the lens keeps
  // `line` set and only flips `open`), so the drawer never blanks mid-close.
  if (!line) {
    return <Drawer open={false} onClose={onClose} width={560} />;
  }

  const batches = docIndex.batchesForLine(line.id);
  const receipts = poRefs.flatMap((po) => docIndex.receiptsForPo(po.poId));
  const invoices = docIndex.invoicesForSo(line.soId);

  const railItems: LinkedDocumentRailItem[] = [
    {
      key: 'so',
      label: t('occ:occ.drawer.rail.so'),
      emptyText: t('occ:occ.drawer.rail.none'),
      links: [
        {
          value: line.soNumber,
          onNavigate: () => navigate(`/auth/sales-orders/${line.soId}`),
        },
      ],
    },
    {
      key: 'po',
      label: t('occ:occ.drawer.rail.po'),
      emptyText: t('occ:occ.drawer.rail.none'),
      links: poRefs.map((po) => ({
        value: po.poNumber,
        onNavigate: () => navigate(`/auth/purchase-orders/${po.poId}`),
      })),
    },
    {
      key: 'batch',
      label: t('occ:occ.drawer.rail.batch'),
      emptyText: t('occ:occ.drawer.rail.none'),
      links: batches.map((batch) => ({
        value: batch.batchNumber,
        onNavigate: () => onOpenBatch(batch.id),
      })),
    },
    {
      key: 'receipt',
      label: t('occ:occ.drawer.rail.receipt'),
      emptyText: t('occ:occ.drawer.rail.none'),
      links: receipts.map((receipt) => ({
        value: receipt.grNumber,
        onNavigate: () => navigate('/auth/goods-receipts'),
      })),
    },
    {
      key: 'invoice',
      label: t('occ:occ.drawer.rail.invoice'),
      emptyText: t('occ:occ.drawer.rail.none'),
      links: invoices.map((invoice) => ({
        value: invoice.invoiceNumber,
        onNavigate: () => navigate('/auth/finance/ar'),
      })),
    },
  ];

  // -- bucket event timeline (facts only; stamps when known) ----------------
  const events: TimelineEvent[] = [
    {
      key: 'ordered',
      label: t('occ:occ.drawer.timeline.ordered'),
      qty: line.qtyOrdered,
      detail: line.soNumber,
    },
  ];

  const poLineIdSet = new Set(line.poLineIds);
  checkpointRecords
    .filter((record) => record.lines.some((row) => poLineIdSet.has(row.poLineId)))
    .forEach((record) => {
      const qty = record.lines
        .filter((row) => poLineIdSet.has(row.poLineId))
        .reduce((sum, row) => sum + row.qtyConfirmed, 0);
      events.push({
        key: `cp-${record.id}`,
        // Sanctioned bilingual checkpoint label, e.g. 「A1 估計」 — never bare codes.
        label: cpLabel(record.checkpoint),
        qty,
        detail: record.poNumber,
        at: record.at,
      });
    });

  if (line.qtyLoaded > 0) {
    const sealed = batches.find((batch) => batch.cutOffAt);
    events.push({
      key: 'loaded',
      label: t('occ:occ.drawer.timeline.loaded'),
      qty: line.qtyLoaded,
      detail: sealed?.batchNumber,
      at: sealed?.cutOffAt ?? null,
    });
  }
  if (line.qtyReceived > 0) {
    const receipt = receipts.find((row) => row.receivedAt);
    events.push({
      key: 'received',
      label: t('occ:occ.drawer.timeline.received'),
      qty: line.qtyReceived,
      detail: receipt?.grNumber,
      at: receipt?.receivedAt ?? null,
    });
  }
  if (line.qtyInvoiced > 0) {
    const invoice = invoices[0];
    events.push({
      key: 'invoiced',
      label: t('occ:occ.drawer.timeline.invoiced'),
      qty: line.qtyInvoiced,
      detail: invoice?.invoiceNumber,
      at: invoice ? invoice.dueDate : null,
    });
  }

  const sectionTitleStyle = {
    fontSize: token.fontSize,
    fontWeight: token.fontWeightStrong,
    marginBottom: token.marginSM,
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      destroyOnHidden
      title={
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{line.sku}</span>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM, fontWeight: 'normal' }}>
            {line.productName} · {line.internalProductId}
          </Text>
        </div>
      }
    >
      {/* Context chips — late-bound 進度點 label + owner */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-6">
        <Tag style={{ margin: 0 }}>{nodeLabel(line.node) || '-'}</Tag>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t('occ:occ.drawer.owner')}: {t(`occ:occ.owner.${line.owner.role}`)}
        </Text>
      </div>

      {line.exception && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: token.marginLG }}
          message={t('occ:occ.drawer.exceptionTitle')}
          description={line.exception.note ?? undefined}
        />
      )}

      {/* Quantity Buckets 數量分段 — drawer-width numerics */}
      <div style={{ marginBottom: token.marginLG }}>
        <Title level={5} style={sectionTitleStyle}>
          {t('occ:occ.drawer.quantities')}
        </Title>
        <QuantityBucketBar
          qtyOrdered={line.qtyOrdered}
          qtyConfirmed={line.qtyConfirmed}
          qtyLoaded={line.qtyLoaded}
          qtyReceived={line.qtyReceived}
          orderBalance={line.orderBalance}
          labels={{
            ordered: t('occ:occ.buckets.ordered'),
            confirmed: t('occ:occ.buckets.confirmed'),
            loaded: t('occ:occ.buckets.loaded'),
            received: t('occ:occ.buckets.received'),
            balance: t('occ:occ.buckets.orderBalance'),
            negativeWarning: t('occ:occ.buckets.negativeWarning'),
          }}
          size="drawer"
        />
      </div>

      {/* 關聯單據 — navigable both ends */}
      <div style={{ marginBottom: token.marginLG }}>
        <Title level={5} style={sectionTitleStyle}>
          {t('occ:occ.drawer.railTitle')}
        </Title>
        <LinkedDocumentRail items={railItems} ariaLabel={t('occ:occ.drawer.railAria')} />
      </div>

      {/* 數量分段事件 timeline — A1/A2/A3 stamps as bilingual labels */}
      <div style={{ marginBottom: token.marginLG }}>
        <Title level={5} style={sectionTitleStyle}>
          {t('occ:occ.drawer.timelineTitle')}
        </Title>
        <Timeline
          items={events.map((event) => ({
            key: event.key,
            children: (
              <div className="tw-flex tw-flex-col tw-gap-0.5">
                <span>
                  {event.label}{' '}
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: token.fontWeightStrong,
                    }}
                  >
                    {event.qty}
                  </span>
                </span>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {[event.detail, event.at ? dayjs(event.at).format('YYYY-MM-DD HH:mm') : null]
                    .filter(Boolean)
                    .join(' · ') || '-'}
                </Text>
              </div>
            ),
          }))}
        />
      </div>

      <LensFooter syncedAt={syncedAt} />
    </Drawer>
  );
};

export default LineDrawer;
